import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import { useModal } from '@/contexts';
import GoogleLogo from '@/assets/google.svg';
import { useI18n } from '@/i18n';

interface HeroSectionProps {
  variant?: 'academic' | 'music';
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6 },
  },
};

const HeroSection: React.FC<HeroSectionProps> = ({ variant = 'academic' }) => {
  const {
    openParentRegistration,
    openTutorRegistration,
    openMusicParentRegistration,
    openMusicTutorRegistration,
  } = useModal();
  const { locale } = useI18n();
  const isMusic = variant === 'music';
  const isZh = locale === 'zh-CN';
  const openFindTutor = isMusic ? openMusicParentRegistration : openParentRegistration;
  const openRegisterTutor = isMusic ? openMusicTutorRegistration : () => openTutorRegistration();

  return (
    <section className={`${isMusic ? 'bg-gradient-to-b from-hero-bg via-white to-white' : 'bg-gradient-to-b from-hero-bg via-blue-50/30 to-white'} text-gray-800 pt-12 md:pt-24 lg:pt-32 pb-4 md:pb-12 lg:pb-16 relative overflow-hidden`}>
      <div className="container mx-auto px-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl mx-auto text-center"
        >
          <motion.h1 variants={itemVariants} className="mb-6 text-5xl font-bold leading-tight tracking-tight text-gray-900 md:text-7xl lg:text-8xl">
            {isMusic ? (
              <span className="text-gray-900">Find the Perfect <span className="text-accent">Music Tutor</span></span>
            ) : (
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {isZh ? '找到最适合的导师' : 'Find the Perfect Tutor'}
              </span>
            )}
            <span className="block text-gray-900">
              {isMusic ? 'for Your Musical Journey' : isZh ? '陪你走好学习每一步' : 'for Your Journey'}
            </span>
          </motion.h1>

          <motion.p variants={itemVariants} className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-gray-600 md:mb-0 md:text-2xl">
            {isMusic
              ? 'Connect with passionate music tutors across Malaysia. Personal lessons for every age, level, and goal.'
              : isZh
                ? '连接马来西亚各地优质导师，让高质量教育更容易被每个家庭获得。'
                : 'Connect with qualified tutors across Malaysia. Quality education made accessible for everyone.'}
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="mx-auto mt-10 hidden w-full max-w-2xl items-center justify-center gap-4 md:flex"
          >
            <button
              onClick={openFindTutor}
              className={`inline-flex min-w-[220px] cursor-pointer items-center justify-center gap-2 rounded-2xl bg-primary px-7 py-3.5 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus-visible:ring-2 ${isMusic ? 'hover:bg-primary-800 focus-visible:ring-primary-300' : 'hover:bg-primary-600 focus-visible:ring-primary/30'}`}
            >
              <span>{isMusic ? 'Find Your Tutor' : isZh ? '寻找导师' : 'Find Your Tutor'}</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </button>

            <button
              onClick={openRegisterTutor}
              className={`inline-flex min-w-[220px] cursor-pointer items-center justify-center rounded-2xl bg-white px-7 py-3.5 text-base font-semibold transition-colors duration-200 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 ${isMusic ? 'border border-gray-900 text-gray-900 focus-visible:ring-primary-300' : 'border border-primary-200 text-primary-900 focus-visible:ring-primary/20'}`}
            >
              {isMusic ? 'Register as tutor' : isZh ? '注册成为导师' : 'Register as tutor'}
            </button>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="mt-5 hidden items-center justify-center gap-3.5 text-lg text-gray-600 md:flex"
          >
            <img src={GoogleLogo} alt="Google" className="h-7 w-7 shrink-0" />
            <span className="text-lg font-semibold text-gray-900">4.9</span>
            <div className="flex items-center gap-1" aria-label="Rated 4.9 out of 5 stars">
              {Array.from({ length: 5 }).map((_, index) => (
                <Star
                  key={index}
                  className="h-6 w-6 fill-[#F4C542] text-[#F4C542]"
                />
              ))}
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="mx-auto mt-8 flex w-full max-w-sm flex-col items-center gap-3 md:hidden">
            <button
              onClick={openFindTutor}
              className={`inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-primary px-7 py-3.5 text-base font-semibold text-white shadow-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 ${isMusic ? 'hover:bg-primary-800 focus-visible:ring-primary-300' : 'hover:bg-primary-600 focus-visible:ring-primary/30'}`}
            >
              <span>{isMusic ? 'Find Your Tutor' : isZh ? '寻找导师' : 'Find Your Tutor'}</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </button>
            <button
              onClick={openRegisterTutor}
              className={`inline-flex w-full cursor-pointer items-center justify-center rounded-2xl bg-white px-7 py-3.5 text-base font-semibold transition-colors duration-200 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 ${isMusic ? 'border border-gray-900 text-gray-900 focus-visible:ring-primary-300' : 'border border-primary-200 text-primary-900 focus-visible:ring-primary/20'}`}
            >
              {isMusic ? 'Register as tutor' : isZh ? '注册成为导师' : 'Register as tutor'}
            </button>
            <div className="mt-1 flex items-center justify-center gap-3.5 text-lg text-gray-600">
              <img src={GoogleLogo} alt="Google" className="h-7 w-7 shrink-0" />
              <span className="text-lg font-semibold text-gray-900">4.9</span>
              <div className="flex items-center gap-1" aria-label="Rated 4.9 out of 5 stars">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    key={index}
                    className="h-6 w-6 fill-[#F4C542] text-[#F4C542]"
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
